"use client";

import { shouldShowElInDate } from "@workspace/shared/date";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useLiveReferenceTime } from "@workspace/ui/lib/use-live-reference-time";

type DateTooltipProps = {
  date: Date | string;
  timestamp?: Date;
  referenceTime?: Date | string;
  className?: string;
  prefixOlderDatesWithEl?: boolean;
};

function parseDateSafe(date: Date | string): Date {
  if (typeof date === "string") {
    // Check if string already contains time information
    if (date.includes("T") || date.includes(" ")) {
      return new Date(date);
    }
    // String is date-only (YYYY-MM-DD), safely append time
    return new Date(`${date}T00:00:00`);
  }
  return new Date(date);
}

function getRelativeTime(
  date: Date | string,
  referenceTime: Date | null
): string {
  // Parse date string in local timezone to avoid UTC conversion issues
  const targetDate = parseDateSafe(date);
  if (!referenceTime) {
    return format(targetDate, "dd/MM/yyyy");
  }
  const today = referenceTime;

  // Compare dates in local timezone
  const targetDateOnly = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const diffTime = todayOnly.getTime() - targetDateOnly.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "hoy";
  }
  if (diffDays === 1) {
    return "ayer";
  }
  // For older dates
  if (targetDate.getFullYear() === today.getFullYear()) {
    // Same year: day + month (e.g. "25 jul")
    return format(targetDate, "d MMM", { locale: es });
  }
  // Past year: dd/mm/yyyy
  return format(targetDate, "dd/MM/yyyy");
}

export function DateTooltip({
  date,
  timestamp,
  referenceTime: initialReferenceTime,
  className,
  prefixOlderDatesWithEl = false,
}: DateTooltipProps) {
  const liveReferenceTime = useLiveReferenceTime();
  const referenceTime =
    liveReferenceTime ??
    (initialReferenceTime ? parseDateSafe(initialReferenceTime) : null);
  const relativeTime = getRelativeTime(date, referenceTime);
  const showElPrefix =
    prefixOlderDatesWithEl &&
    (referenceTime === null || shouldShowElInDate(date, referenceTime));
  // Handle date formatting with timezone considerations
  const dateToFormat = parseDateSafe(date);
  const formattedDate = format(dateToFormat, "dd/MM/yyyy");
  const formattedTimestamp = timestamp
    ? format(new Date(timestamp), "dd/MM/yyyy HH:mm")
    : null;

  return (
    <Tooltip>
      <TooltipTrigger className={className}>
        {showElPrefix && "el "}
        {relativeTime}
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs">
          <div>
            <span className="text-primary-foreground/50">Fecha:</span>{" "}
            {formattedDate}
          </div>
          {formattedTimestamp && (
            <div>
              <span className="text-primary-foreground/50">Creado el:</span>{" "}
              {formattedTimestamp}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
